import React, { useEffect, useState } from 'react';
import { Activity, ArrowUpRight, Clock, ShieldAlert } from 'lucide-react';
import { statusApi, formatUptime, formatResponseTime } from '../utils/api';
import { SystemStatus } from '../types';

/**
 * Read-only dashboard for roles that cannot manage monitors (viewer, user).
 *
 * The monitor CRUD endpoints are admin-only, so a non-admin must not call them.
 * Everything shown here comes from /api/status, which is public.
 */
export const ViewerDashboard: React.FC = () => {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const data = await statusApi.getSystemStatus();
        if (!cancelled) {
          setStatus(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load status');
        }
      }
    };

    load();
    const interval = window.setInterval(load, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">PingPal Dashboard</h1>
          <p className="text-base-content/70">
            Read-only view. Your account can watch status but not edit monitors.
          </p>
        </div>
      </div>

      <div className="alert alert-info mb-6">
        <ShieldAlert className="w-5 h-5" />
        <span>
          Monitor management, alerting and user settings need an admin account.
          Ask an admin to change your role if you need to edit anything.
        </span>
      </div>

      {error && (
        <div className="alert alert-error mb-6">
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="stat bg-base-100 shadow-lg rounded-lg">
          <div className="stat-figure text-primary">
            <Activity className="w-8 h-8" />
          </div>
          <div className="stat-title">Monitors</div>
          <div className="stat-value text-primary">{status?.total_monitors ?? '-'}</div>
          <div className="stat-desc">{status?.active_monitors ?? 0} active</div>
        </div>
        <div className="stat bg-base-100 shadow-lg rounded-lg">
          <div className="stat-figure text-info">
            <ArrowUpRight className="w-8 h-8" />
          </div>
          <div className="stat-title">Overall Uptime</div>
          <div className="stat-value text-info">
            {status ? formatUptime(status.overall_uptime) : '-'}
          </div>
          <div className="stat-desc">Average across all monitors</div>
        </div>
        <div className="stat bg-base-100 shadow-lg rounded-lg">
          <div className="stat-figure text-warning">
            <Clock className="w-8 h-8" />
          </div>
          <div className="stat-title">Avg Response</div>
          <div className="stat-value text-warning">
            {status ? formatResponseTime(status.average_response_time) : '-'}
          </div>
          <div className="stat-desc">Average response time</div>
        </div>
      </div>

      <div className="overflow-x-auto bg-base-100 rounded-lg shadow-lg">
        <table className="table">
          <thead>
            <tr>
              <th>Monitor</th>
              <th>Status</th>
              <th>Uptime</th>
              <th>Last response</th>
            </tr>
          </thead>
          <tbody>
            {(status?.monitors_status ?? []).map((entry) => (
              <tr key={entry.monitor.id}>
                <td>
                  <div className="font-medium">{entry.monitor.name}</div>
                  <div className="text-xs opacity-60 break-all">{entry.monitor.url}</div>
                </td>
                <td>
                  <span className={`badge ${entry.is_currently_up ? 'badge-success' : 'badge-error'}`}>
                    {entry.is_currently_up ? 'UP' : 'DOWN'}
                  </span>
                </td>
                <td>{formatUptime(entry.uptime_percentage)}</td>
                <td>{formatResponseTime(entry.last_ping?.response_time ?? 0)}</td>
              </tr>
            ))}
            {!status?.monitors_status?.length && (
              <tr>
                <td colSpan={4} className="text-center opacity-60 py-6">
                  No monitors reported yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ViewerDashboard;
